<template>
    <panel :title="$t('agent.coworker.title')">
        <template #list>
            <!-- Search -->
            <v-text-field
                v-model="search"
                :placeholder="$t('agent.sidePanel.search')"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mx-2 mb-3"
            />

            <!-- Designer filters -->
            <div class="px-2 mb-3 d-flex flex-column ga-2">
                <v-select
                    v-model="selectedTeam"
                    :items="teamOptions"
                    :label="$t('agent.sidePanel.filters.team')"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                />
                <v-select
                    v-model="selectedSubTeam"
                    :items="subTeamOptions"
                    :label="$t('agent.sidePanel.filters.subTeam')"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    :disabled="subTeamOptions.length === 0"
                />
                <v-select
                    v-model="selectedVisibility"
                    :items="visibilityOptions"
                    :label="$t('agent.sidePanel.filters.visibility')"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                />
            </div>

            <!-- Section header -->
            <div v-if="filteredOwnAgents.length > 0" class="d-flex align-center justify-space-between px-3 mb-1">
                <span class="text-caption text-medium-emphasis font-weight-medium text-uppercase" style="letter-spacing: 0.5px">
                    {{ $t('agent.coworker.list.title') }}
                </span>
                <span class="text-caption text-medium-emphasis">{{ filteredOwnAgents.length }}</span>
            </div>

            <!-- Grouped agent list -->
            <div v-for="group in groupedVisibleOwnAgents" :key="group.key" class="mb-2">
                <div class="d-flex align-center justify-space-between px-3 mb-1">
                    <span class="text-caption text-medium-emphasis">{{ group.label }}</span>
                    <span class="text-caption text-medium-emphasis">{{ group.agents.length }}</span>
                </div>
                <v-list density="compact" nav class="py-0">
                    <v-list-item
                        v-for="agent in group.agents"
                        :key="agent.id"
                        :active="store.loadedAgentId === agent.id"
                        @click="$emit('loadAgent', agent)"
                        class="agent-item rounded-lg mb-0"
                        :class="{ 'status-border-warning': agent.status === 'pending', 'status-border-error': agent.status === 'rejected' || agent.status === 'revoked', 'status-border-grey': agent.status === 'draft' }"
                    >
                        <template #prepend>
                            <v-icon
                                size="18"
                                :color="agent.status === 'approved' ? 'success' : (statusMeta[agent.status]?.color || 'grey')"
                                class="mr-3"
                            >
                                {{ agent.status === 'approved' ? 'mdi-check-circle' : (statusMeta[agent.status]?.icon || 'mdi-circle-outline') }}
                            </v-icon>
                        </template>

                        <v-list-item-title class="text-body-2 font-weight-medium">
                            {{ agent.name }}
                            <v-icon v-if="agent.is_system" size="14" class="ml-1" color="deep-purple"
                                ><v-tooltip activator="parent" location="bottom">{{ $t('agent.sidePanel.systemAgent') }}</v-tooltip
                                >mdi-shield-star-outline</v-icon
                            >
                            <v-icon v-else-if="agent.is_global" size="14" class="ml-1" color="amber-darken-2"
                                ><v-tooltip activator="parent" location="bottom">{{ $t('agent.sidePanel.globalAgent') }}</v-tooltip
                                >mdi-earth</v-icon
                            >
                        </v-list-item-title>

                        <v-list-item-subtitle class="text-caption text-truncate">
                            {{ formatAgentSubtitle(agent) }}
                        </v-list-item-subtitle>

                        <template #append>
                            <div class="agent-actions d-flex align-center">
                                <v-btn icon size="x-small" variant="text" @click.stop="$emit('editAgent', agent)">
                                    <v-icon size="14">mdi-pencil</v-icon>
                                    <v-tooltip activator="parent" location="bottom">{{ $t('agent.coworker.dialog.editTitle') }}</v-tooltip>
                                </v-btn>
                                <v-btn icon size="x-small" variant="text" color="error" @click.stop="$emit('deleteAgent', agent)">
                                    <v-icon size="14">mdi-delete</v-icon>
                                    <v-tooltip activator="parent" location="bottom">{{ $t('agent.coworker.dialog.deleteTitle') }}</v-tooltip>
                                </v-btn>
                            </div>
                        </template>
                    </v-list-item>
                </v-list>
            </div>

            <!-- Show more / less -->
            <div v-if="filteredOwnAgents.length > DEFAULT_VISIBLE" class="text-center mt-1 mb-2">
                <v-btn
                    variant="text"
                    size="small"
                    density="compact"
                    color="primary"
                    @click="showAll = !showAll"
                >
                    <v-icon size="16" class="mr-1">{{ showAll ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    {{ showAll ? $t('agent.sidePanel.showLess') : $t('agent.sidePanel.showMore', { count: filteredOwnAgents.length - DEFAULT_VISIBLE }) }}
                </v-btn>
            </div>

            <!-- Phonebook results (only when searching) -->
            <template v-if="normalizedSearch && filteredPhonebook.length > 0">
                <v-divider class="my-2 mx-2" />
                <div class="d-flex align-center justify-space-between px-3 mb-1">
                    <span class="text-caption text-medium-emphasis font-weight-medium text-uppercase" style="letter-spacing: 0.5px">
                        {{ $t('agent.coworker.phonebook.title') }}
                    </span>
                    <span class="text-caption text-medium-emphasis">{{ filteredPhonebook.length }}</span>
                </div>
                <v-list density="compact" nav class="py-0">
                    <v-list-item
                        v-for="entry in filteredPhonebook"
                        :key="'pb-' + entry.name"
                        :disabled="!entry.compatible"
                        class="agent-item rounded-lg mb-0"
                    >
                        <v-list-item-title class="text-body-2">
                            {{ entry.name }}
                            <v-icon v-if="entry.is_global" size="14" class="ml-1 text-medium-emphasis">mdi-earth</v-icon>
                        </v-list-item-title>

                        <v-list-item-subtitle class="text-caption">
                            <span v-if="entry.compatible">{{ formatPhonebookSubtitle(entry) }}</span>
                            <span v-else class="text-error">⚠ {{ $t('agent.coworker.phonebook.incompatible') }}</span>
                        </v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </template>

            <!-- Empty state -->
            <div v-if="filteredOwnAgents.length === 0 && (!normalizedSearch || filteredPhonebook.length === 0)" class="text-body-2 text-medium-emphasis px-3 py-8 text-center">
                <div>{{ normalizedSearch ? $t('agent.sidePanel.noResults') : $t('agent.coworker.list.empty') }}</div>
            </div>
        </template>
    </panel>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import Panel from "@/components/generics/Panel/Panel.vue";
import { useAgentStore, type SubAgent, type SubAgentPhonebookEntry } from "@/stores/AgentStore/AgentStore";
import { useI18n } from "vue-i18n";

const DEFAULT_VISIBLE = 10;

const STATUS_META: Record<string, { icon: string; color: string; key: string }> = {
    pending: { icon: "mdi-clock-outline", color: "warning", key: "pending" },
    rejected: { icon: "mdi-close-circle-outline", color: "error", key: "rejected" },
    draft: { icon: "mdi-pencil-outline", color: "grey", key: "draft" },
    revoked: { icon: "mdi-cancel", color: "grey-darken-1", key: "revoked" },
};

export default defineComponent({
    components: { Panel },
    emits: ['loadAgent', 'editAgent', 'deleteAgent'],
    setup() {
        const store = useAgentStore();
        const { t } = useI18n();
        const search = ref("");
        const showAll = ref(false);
        const selectedTeam = ref<string | null>(null);
        const selectedSubTeam = ref<string | null>(null);
        const selectedVisibility = ref<string | null>(null);

        const statusMeta = computed(() => {
            const result: Record<string, { icon: string; color: string; label: string }> = {};
            for (const [status, meta] of Object.entries(STATUS_META)) {
                result[status] = {
                    icon: meta.icon,
                    color: meta.color,
                    label: t(`agent.sidePanel.status.${meta.key}`),
                };
            }
            return result;
        });

        const normalizedSearch = computed(() => (search.value || "").toLowerCase().trim());

        const normalize = (value?: string | null): string => {
            const normalized = (value || '').trim().toLowerCase();
            return normalized === 'application' ? 'team' : normalized;
        };

        const toDiscoveryFilters = () => {
            const team = (selectedTeam.value || '').trim();
            const sub_team = (selectedSubTeam.value || '').trim();
            const visibility = (selectedVisibility.value || '').trim();
            return {
                team: team || undefined,
                sub_team: sub_team || undefined,
                visibility: normalize(visibility) || undefined,
            };
        };

        const matchesDesignerFilters = (entry: { team?: string | null; sub_team?: string | null; visibility?: string | null }): boolean => {
            if (selectedTeam.value && normalize(entry.team) !== normalize(selectedTeam.value)) return false;
            if (selectedSubTeam.value && normalize(entry.sub_team) !== normalize(selectedSubTeam.value)) return false;
            if (selectedVisibility.value && normalize(entry.visibility) !== normalize(selectedVisibility.value)) return false;
            return true;
        };

        // Reset expand when searching
        watch(normalizedSearch, () => { showAll.value = false; });

        watch([selectedTeam, selectedSubTeam, selectedVisibility], async () => {
            await store.loadPhonebook(toDiscoveryFilters());
            showAll.value = false;
        });

        watch(selectedTeam, () => {
            if (!selectedSubTeam.value) return;
            const exists = subTeamOptions.value.some(option => option.value === selectedSubTeam.value);
            if (!exists) selectedSubTeam.value = null;
        });

        const visibilityOptions = computed(() => {
            const base = [
                { title: 'Private', value: 'private' },
                { title: 'Team', value: 'team' },
                { title: 'Global', value: 'global' },
            ];
            const dynamic = Array.from(new Set([
                ...store.subAgents.map(agent => normalize(agent.visibility)).filter(Boolean),
                ...store.phonebook.map(agent => normalize(agent.visibility)).filter(Boolean),
            ]));
            const knownValues = new Set(base.map(item => item.value));
            const extras = dynamic
                .filter(value => !knownValues.has(value.toLowerCase()))
                .sort((a, b) => a.localeCompare(b))
                .map(value => ({ title: value, value }));
            return [...base, ...extras];
        });

        const teamOptions = computed(() => {
            const values = Array.from(new Set([
                ...store.subAgents.map(agent => (agent.team || '').trim()).filter(Boolean),
                ...store.phonebook.map(agent => (agent.team || '').trim()).filter(Boolean),
            ]));
            return values.sort((a, b) => a.localeCompare(b)).map(value => ({ title: value, value }));
        });

        const subTeamOptions = computed(() => {
            const selectedTeamNormalized = normalize(selectedTeam.value);
            const ownSubTeams = store.subAgents
                .filter(agent => !selectedTeamNormalized || normalize(agent.team) === selectedTeamNormalized)
                .map(agent => (agent.sub_team || '').trim())
                .filter(Boolean);
            const phonebookSubTeams = store.phonebook
                .filter(agent => !selectedTeamNormalized || normalize(agent.team) === selectedTeamNormalized)
                .map(agent => (agent.sub_team || '').trim())
                .filter(Boolean);
            const values = Array.from(new Set([...ownSubTeams, ...phonebookSubTeams]));
            return values.sort((a, b) => a.localeCompare(b)).map(value => ({ title: value, value }));
        });

        const filteredOwnAgents = computed(() => {
            const filteredByMeta = store.subAgents.filter(matchesDesignerFilters);
            if (!normalizedSearch.value) return filteredByMeta;
            return filteredByMeta.filter(a =>
                a.name.toLowerCase().includes(normalizedSearch.value) ||
                (a.description || "").toLowerCase().includes(normalizedSearch.value) ||
                a.tags.some(tag => tag.toLowerCase().includes(normalizedSearch.value))
            );
        });

        const visibleOwnAgents = computed(() => {
            if (showAll.value || normalizedSearch.value) return filteredOwnAgents.value;
            return filteredOwnAgents.value.slice(0, DEFAULT_VISIBLE);
        });

        const groupKey = (agent: SubAgent): string => `${(agent.team || '').trim()}::${(agent.sub_team || '').trim()}`;

        const groupLabel = (agent: SubAgent): string => {
            const team = (agent.team || '').trim() || t('agent.sidePanel.groups.noTeam');
            const subTeam = (agent.sub_team || '').trim();
            return subTeam ? `${team} / ${subTeam}` : team;
        };

        const groupedVisibleOwnAgents = computed(() => {
            const groups = new Map<string, { key: string; label: string; agents: SubAgent[] }>();
            for (const agent of visibleOwnAgents.value) {
                const key = groupKey(agent);
                if (!groups.has(key)) {
                    groups.set(key, { key, label: groupLabel(agent), agents: [] });
                }
                groups.get(key)?.agents.push(agent);
            }

            const result = Array.from(groups.values());
            for (const group of result) {
                group.agents.sort((a, b) => a.name.localeCompare(b.name));
            }
            result.sort((a, b) => a.label.localeCompare(b.label));
            return result;
        });

        const formatAgentSubtitle = (agent: SubAgent): string => {
            const parts: string[] = [];
            if (agent.model) parts.push(agent.model);
            if (agent.visibility) parts.push(agent.visibility);
            return parts.join(' · ') || '—';
        };

        const formatPhonebookSubtitle = (entry: SubAgentPhonebookEntry): string => {
            const parts: string[] = [];
            if (entry.model) parts.push(entry.model);
            if (entry.team) parts.push(entry.team);
            if (entry.sub_team) parts.push(entry.sub_team);
            if (entry.visibility) parts.push(entry.visibility);
            if (entry.description) parts.push(entry.description);
            return parts.join(' · ') || '—';
        };

        const filteredPhonebook = computed(() => {
            if (!normalizedSearch.value) return [];
            return store.phonebook.filter(e =>
                matchesDesignerFilters(e) &&
                (
                    e.name.toLowerCase().includes(normalizedSearch.value) ||
                    (e.description || "").toLowerCase().includes(normalizedSearch.value) ||
                    e.tags.some(tag => tag.toLowerCase().includes(normalizedSearch.value))
                )
            );
        });

        return {
            store,
            search,
            showAll,
            selectedTeam,
            selectedSubTeam,
            selectedVisibility,
            normalizedSearch,
            statusMeta,
            visibilityOptions,
            teamOptions,
            subTeamOptions,
            filteredOwnAgents,
            visibleOwnAgents,
            groupedVisibleOwnAgents,
            filteredPhonebook,
            formatAgentSubtitle,
            formatPhonebookSubtitle,
            DEFAULT_VISIBLE,
        };
    },
});
</script>

<style lang="scss" scoped>
.agent-item {
    border-left: 3px solid transparent;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &.status-border-warning {
        border-left-color: rgb(var(--v-theme-warning));
    }

    &.status-border-error {
        border-left-color: rgb(var(--v-theme-error));
    }

    &.status-border-grey {
        border-left-color: #9e9e9e;
    }

    .agent-actions {
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    &:hover .agent-actions {
        opacity: 1;
    }
}
</style>
